import { IModalProps, Modal } from '@/react/components/core-ui/Modal';
import { ModalHeader } from '@/react/components/core-ui/Modal/ModalHeader';
import { useRef, type FC } from 'react';

import { observer } from 'mobx-react-lite';

import { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import {
  ContentType,
  type Content
} from '@/services/isomorphic/ContentService';
import type { Callback, VoidCallback } from '@/type-utils';
import { IconTypes } from '@/react/components/core-ui/Icon';
import { FetchContentWhenActive } from '@/react/components/content/core/FetchContentWhenActive';
import { Spinner } from '@/react/components/core-ui/Spinner';
import { SearchModalInput } from './SearchModalInput';
import { SearchSuggestions } from '../../../SearchSuggestions';
import { ProductSuggestions } from '../../../ProductSuggestions';

import S from './styles.base.module.scss';

/**
 * Props for the MobileSearchModal component.
 *
 * This type extends the IModalProps type, but omits unused properties.
 */
interface ISearchModalProps
  extends Omit<
    IModalProps,
    'isPane' | 'panePosition' | 'isFullScreen' | 'children'
  > {
  /**
   * The search box view model.
   *
   * This will be used to manage the state of the search input and perform the search.
   */
  vm: SearchBoxVM;
  /**
   * A callback function to be called when the search input is cleared.
   */
  onClear: VoidCallback;
  /**
   * A callback function to be called when the search navigation is performed.
   */
  onNavigate: VoidCallback;
  /**
   * A callback function to be called when the search is performed.
   */
  onSearch: Callback<[string], Promise<void>>;
  /**
   * The content to display in the custom search content section.
   *
   * This will be fetched from the ContentService when the modal is opened.
   */
  content?: Content | null;
  /**
   * Whether the search content is loading.
   *
   * This will be used to show a loading state while the custom search content is being fetched.
   */
  loading?: boolean;
  /**
   * The content ID of the modal.
   */
  contentID?: string;
  /**
   * The content type of the modal.
   */
  contentType?: ContentType.Fragment | ContentType.Page | ContentType.Tile;
}

/**
 * A modal that is intended for mobile search.
 */
export const SearchModal: FC<ISearchModalProps> = observer(
  ({
    vm,
    onClear,
    onSearch,
    onNavigate,
    contentID = 'custom-search-content',
    contentType = ContentType.Fragment,
    ...props
  }) => {
    const { onClose, onOpenFinished: externalOnOpenFinished, isOpen } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleOpenFinished: VoidCallback = () => {
      // Call any external hook, then ensure focus for a11y
      externalOnOpenFinished?.();
      inputRef.current?.focus();
    };

    return (
      <Modal isFullScreen onOpenFinished={handleOpenFinished} {...props}>
        <ModalHeader
          onClose={onClose}
          className={S.searchModalHeader}
          closeIcon={IconTypes.CloseLight}
        />
        <div className={S.searchModalContainer}>
          <SearchModalInput
            inputRef={inputRef}
            vm={vm}
            onSearch={onSearch}
            onNavigate={onNavigate}
            onClear={onClear}
          />
          <SearchSuggestions vm={vm} onNavigate={onClose} />
          <ProductSuggestions
            vm={vm}
            // Here navigation is handled by `<Link />` component, so we don't need to
            // programmatically do the `onNavigate` call.
            //
            // Instead, we just close the modal when a product suggestion is clicked.
            onNavigate={onClose}
          />
        </div>
        <div className={S.content}>
          <FetchContentWhenActive
            options={{ id: contentID, type: contentType }}
            isActive={isOpen}
            fallback={<Spinner />}
            handleLinkClick={onClose}
          />
        </div>
      </Modal>
    );
  }
);
